/**
 * Standardized unit measure options for product sizing.
 * Bilingual labels (English / Spanish) for bakery staff.
 */
export const UNIT_MEASURE_OPTIONS = [
  { value: '', label: '-- None / Ninguno --' },
  { value: 'lb', label: 'Pound / Libra' },
  { value: 'oz', label: 'Ounce / Onza' },
  { value: 'kg', label: 'Kilogram / Kilo' },
  { value: 'g', label: 'Gram / Gramo' },
  { value: 'each', label: 'Each / Cada uno' },
  { value: 'piece', label: 'Piece / Pieza' },
  { value: 'box', label: 'Box / Caja' },
  { value: 'case', label: 'Case / Caja grande' },
  { value: 'bag', label: 'Bag / Bolsa' },
  { value: 'gallon', label: 'Gallon / Galon' },
  { value: 'liter', label: 'Liter / Litro' },
  { value: 'dozen', label: 'Dozen / Docena' },
];

const UNIT_MEASURE_LABEL_BY_VALUE = UNIT_MEASURE_OPTIONS.reduce((acc, option) => {
  if (option.value) {
    acc[option.value] = option.label;
  }
  return acc;
}, {});

/**
 * Get display label for a unit measure value.
 * Returns the English portion of the label for display.
 * @param {string} value - The unit measure value
 * @returns {string} English label or original value if not found
 */
export function getUnitMeasureLabel(value) {
  if (!value) return '';
  const fullLabel = UNIT_MEASURE_LABEL_BY_VALUE[value];
  return fullLabel ? fullLabel.split(' / ')[0] : value;
}

/**
 * Check if a value is a valid unit measure.
 * @param {string} value - The value to check
 * @returns {boolean} True if valid
 */
export function isValidUnitMeasure(value) {
  if (!value) return true; // Empty is valid (optional field)
  return UNIT_MEASURE_OPTIONS.some(opt => opt.value === value);
}

/**
 * Normalize old free-text unitSize to standardized unitMeasure.
 * Returns null if no match found.
 * @param {string} oldValue - The old unitSize string
 * @returns {string|null} Normalized value or null
 */
export function normalizeOldUnitSize(oldValue) {
  if (!oldValue) return null;

  const normalized = oldValue.toLowerCase().trim();

  const mappings = {
    'lb': 'lb',
    'lbs': 'lb',
    'pound': 'lb',
    'pounds': 'lb',
    'libra': 'lb',
    'libras': 'lb',
    'oz': 'oz',
    'ounce': 'oz',
    'ounces': 'oz',
    'onza': 'oz',
    'onzas': 'oz',
    'kg': 'kg',
    'kilo': 'kg',
    'kilos': 'kg',
    'kilogram': 'kg',
    'g': 'g',
    'gram': 'g',
    'grams': 'g',
    'gramo': 'g',
    'gallon': 'gallon',
    'gal': 'gallon',
    'galon': 'gallon',
    'liter': 'liter',
    'litre': 'liter',
    'litro': 'liter',
    'dozen': 'dozen',
    'docena': 'dozen',
    'doz': 'dozen',
    'each': 'each',
    'ea': 'each',
    'pieza': 'piece',
    'piece': 'piece',
    'box': 'box',
    'caja': 'box',
    'case': 'case',
    'bag': 'bag',
    'bolsa': 'bag',
  };

  // Check for exact match
  if (mappings[normalized]) {
    return mappings[normalized];
  }

  // Check for patterns like "40 lb" - extract the unit part
  const match = normalized.match(/^\d+\s*(.+)$/);
  if (match) {
    const unitPart = match[1].trim();
    if (mappings[unitPart]) {
      return mappings[unitPart];
    }
  }

  return null;
}
